import { registerProfileStateReset } from '@/auth/session';
import i18n from '@/i18n';
import {
  SurveyDetailState,
  SurveyDisplayStatus,
  SurveyResultsAccessType,
  SurveyStatus,
  SurveyUserStatus,
} from '@/types/components';
import type {
  Survey,
  SurveyFillQuestion,
  SurveyQuestionResponse,
  SurveyResponse,
  SurveyResults,
  SurveyResultsResponse,
} from '@/types/survey';
import { formatShortDate, formatTime } from '@/utils/date';

const { Active, Finished } = SurveyStatus;

const finishedSurveyIds = new Set<string>();

registerProfileStateReset(() => finishedSurveyIds.clear());

export const markSurveyFinishedLocally = (surveyId: string) => {
  finishedSurveyIds.add(surveyId);
};

const getDisplayStatus = ({
  status,
  currentUserStatus,
}: SurveyResponse): SurveyDisplayStatus | null => {
  if (status !== Active && status !== Finished) {
    return null;
  }

  const isSurveyOpen = status === Active;

  switch (currentUserStatus) {
    case SurveyUserStatus.New:
    case SurveyUserStatus.Started:
      return isSurveyOpen
        ? SurveyDisplayStatus.ToFill
        : SurveyDisplayStatus.Closed;
    case SurveyUserStatus.Finished:
      return SurveyDisplayStatus.Filled;
    case SurveyUserStatus.Completed:
    case SurveyUserStatus.Deleted:
      return null;
  }
};

const getDetailState = ({
  status,
  currentUserStatus,
  surveyResultsAccessType,
}: SurveyResponse): SurveyDetailState => {
  const areResultsAnonymized =
    surveyResultsAccessType === SurveyResultsAccessType.Anonymized;
  const resultsState = areResultsAnonymized
    ? SurveyDetailState.Results
    : SurveyDetailState.Restricted;

  if (status !== Active && status !== Finished) {
    return SurveyDetailState.Restricted;
  }

  if (
    currentUserStatus === SurveyUserStatus.Completed ||
    currentUserStatus === SurveyUserStatus.Deleted
  ) {
    return SurveyDetailState.Restricted;
  }

  if (status === Finished || currentUserStatus === SurveyUserStatus.Finished) {
    return resultsState;
  }

  if (currentUserStatus === SurveyUserStatus.Started) {
    return SurveyDetailState.Continue;
  }

  return SurveyDetailState.New;
};

const formatDeadline = (iso: string): string => {
  const date = new Date(iso);
  const monthsInDative = i18n.t('mail.surveys.deadlineMonths', {
    returnObjects: true,
  });

  return `${date.getDate()}. ${monthsInDative[date.getMonth()]} ${formatTime(iso)}`;
};

export const toSurvey = (rawResponse: SurveyResponse): Survey => {
  const response = finishedSurveyIds.has(rawResponse.id)
    ? { ...rawResponse, currentUserStatus: SurveyUserStatus.Finished }
    : rawResponse;

  return {
    id: response.id,
    isNew: response.isNew ?? false,
    displayStatus: getDisplayStatus(response),
    anonymity: response.surveyAnonymityType,
    detailState: getDetailState(response),
    title: response.title,
    authorName: response.authorName,
    date: formatShortDate(response.timePublished),
    deadline: formatDeadline(response.timeEnded),
    hasResults:
      response.surveyResultsAccessType === SurveyResultsAccessType.Anonymized,
    questionCount: response.questionCount,
    questionIds: (response.questions ?? []).map(({ questionId }) => questionId),
  };
};

export const toSurveyFillQuestion = (
  response: SurveyQuestionResponse,
): SurveyFillQuestion => ({
  id: response.id,
  type: response.type,
  description: response.description,
  isOtherAnswerEnabled: response.isOtherAnswerOptionEnabled,
  answerOptions: response.questionAnswerOptions,
});

export const toSurveyResults = (
  response: SurveyResultsResponse,
): SurveyResults => ({
  respondeesCount: response.countOfRespondees,
  finishedRespondeesCount: response.countOfFinishedRespondees,
  finishedPercentage: response.percentageOfFinishedRespondees,
  questionCount: response.questionCount,
  questions: response.questions.map((question) => ({
    type: question.questionType,
    description: question.description,
    answers: question.answers.map((answer) => ({
      description: answer.description,
      percentage: answer.percentageOfAllAnswers,
      count: answer.countOfAllAnswers,
      isSelectedByCurrentUser: answer.isSelectedByCurrentUser,
      isOtherAnswer: answer.isOtherAnswer,
    })),
  })),
});

export const toSurveys = (responses: SurveyResponse[]): Survey[] =>
  responses.map(toSurvey);
